'use client';

import { FC, useEffect, useContext, useState } from 'react';
import { EntityTagsDialogProps } from './EntityTagsDialog.types';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Icon,
  Text,
} from '@/_components';
import styles from './EntityTagsDialog.module.css';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { addEntityTag, removeEntityTag } from '@/_api/admin-tags';
import { AddEntityTagForm } from './components/AddEntityTagForm';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';

export const EntityTagsDialog: FC<EntityTagsDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [isAdding, setAdding] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [deletingTagId, setDeletingTagId] = useState<number | undefined>(
    undefined,
  );

  const { token } = useContext(UserContext);
  const { firm, reloadFirm } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);

  useEffect(() => {
    return () => setAdding(false);
  }, [isOpen]);

  const onToggleAdding = () => setAdding(!isAdding);
  const hasEntityTags: boolean = !!firm?.entity_tags?.length;

  const onConfirm = async (newTag: string) => {
    try {
      setLoading(true);
      setAdding(false);

      const { error } = await addEntityTag({ new_tag: newTag, token });

      if (error) {
        addToast('error', error, { error });
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      reloadFirm();
      setLoading(false);
    }
  };

  const onRemoveTag = async (tagId: number) => {
    try {
      setDeletingTagId(tagId);
      await removeEntityTag({ tag_id: tagId, token });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      reloadFirm();
      setDeletingTagId(undefined);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Entity Tags">
      <Dialog.Content>
        {isAdding ? (
          <AddEntityTagForm onCancel={onToggleAdding} onConfirm={onConfirm} />
        ) : (
          <Flex isColumn={true} gap={8}>
            {!hasEntityTags && (
              <Alert type="info" icon="info">
                There are no entity tags yet.
              </Alert>
            )}
            <Flex isWrapping={true} gap={4}>
              {firm?.entity_tags?.map((tag, key) => {
                const onDelete = () => onRemoveTag(tag.id);

                return (
                  <Flex
                    key={key}
                    isBetween={true}
                    gap={4}
                    className={styles.entityTag}
                  >
                    <Flex isYCentered={true} gap={4}>
                      <Icon name="tag" size={16} />
                      <Text type="small" fontWeight="medium">
                        {tag.name}
                      </Text>
                    </Flex>
                    <ButtonGroup>
                      <Button
                        intent="danger-secondary"
                        iconLeft="trash"
                        isDisabled={deletingTagId === tag.id}
                        isLoading={deletingTagId === tag.id}
                        value="Remove Tag"
                        isIconOnly={true}
                        size="tiny"
                        onClick={onDelete}
                      />
                    </ButtonGroup>
                  </Flex>
                );
              })}
            </Flex>
            <ButtonGroup align="right">
              <Button
                value="New Entity Tag"
                onClick={onToggleAdding}
                isDisabled={isLoading}
                isLoading={isLoading}
                intent={hasEntityTags ? 'secondary' : 'primary'}
                iconLeft="plus"
              />
            </ButtonGroup>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
